// MEM-1 — **LALA REMEMBERS EACH MEMBER'S OWN PREVIOUS CONVERSATIONS.**
//
// Her order, 2026-09-01: *"a per-member memory layer (a rolling summary of past threads plus an index
// of the situations, people and questions they have brought, refreshed as conversations close) riding
// into her context on every new conversation, so she can say 'last time you were worried about the
// job, how did that land?' and never make a member repeat herself."*
//
// HER FIVE LAWS RIDE, AND EACH IS ANSWERED SOMEWHERE SPECIFIC:
//   1. STRICTLY MEMBER-SCOPED SERVER-SIDE — every statement in the store carries `user_id` in its
//      WHERE clause, and nothing here takes a memory from a request body.
//   2. PERSISTENCE UNTIL SHE DELETES IT — a forget control per item AND a clear-all. Both halves.
//   3. THE ACCURACY GATE — she recalls what was SAID, never invents a past. `memoryClaimUnsupported`
//      is the deterministic half of that, and the injected block carries the instruction.
//   4. NOTHING IN MEMORY EVER NARRATES MACHINERY — *"I remember you told me"*, never *"my records
//      show"*. `memoryNarrationViolations` is the roster of the phrasings that break it.
//   5. COST-CONSCIOUS — summaries are generated ONCE at thread close and never by re-reading raw
//      turns each time. `memoryNeedsSummary` is the whole decision, and it is a pure function so a
//      fixture can drive every arm of it without a pool or a key.
//
// >>> **THIS FILE IMPORTS NOTHING** (R146), which is the entry requirement that lets a `.mjs` gate
// LOAD and RUN every clause rather than certify a spelling with a regex.

/**
 * HER THREE KINDS, DECLARED ONCE. Her sentence names them: *"the situations, people and questions
 * they have brought"*. The store, the prompt block and her forget list all read this roster, so a
 * fourth kind joins by being DECLARED rather than by three lists being kept in step (R28/R29).
 */
export const MEMORY_KINDS = Object.freeze([
  Object.freeze({ key: "situation", label: "What she is going through", lead: "She has been dealing with" }),
  Object.freeze({ key: "person", label: "People in her life", lead: "In her life" }),
  Object.freeze({ key: "question", label: "What she has asked about", lead: "She has asked about" }),
  // >>> **MEM-1 PRESENCE, HER CLAUSE 5 (2026-09-03): THE OPEN LOOPS.** <<< *"The memory summaries
  // gain an OPEN-LOOPS field (the undone things, the promised actions, the awaited outcomes) so her
  // initiative has structure to draw from, written at thread close like the rest."*
  //
  // **IT IS A FOURTH KIND RATHER THAN A COLUMN, AND THAT IS THE SAME INFORMATION WITH FAR LESS
  // MACHINERY.** A kind rides the store, the block, the ledger, the forget door, the accuracy gate
  // and the summariser BY EXISTING (R11/R31); a column on the summary row would need a migration, a
  // writer, a reader and a second answer to *what does this house remember* (R28/R29). Her sentence
  // is about the STRUCTURE her initiative draws from, and this is that structure.
  //
  // **THE LEAD IS A SENTENCE ABOUT HER, NOT A LABEL.** *"She said she was going to"* is what makes
  // the follow-up instinct legible to the model: an open loop is a thing SHE said she would do, so
  // asking how it went is a friend tracking her life rather than a system reading a task list.
  Object.freeze({ key: "loop", label: "What she said she would do", lead: "She said she was going to" }),
]);

export const MEMORY_KIND_KEYS = Object.freeze(MEMORY_KINDS.map((k) => k.key));

export const memoryKind = (key) => MEMORY_KINDS.find((k) => k.key === String(key || "").trim().toLowerCase()) || null;

/** A memory item is one fact in one line. Long enough to mean something, short enough to be a note. */
export const MEMORY_SUBJECT_MAX = 120;
export const MEMORY_DETAIL_MAX = 400;
/** The rolling summary of a whole thread. One paragraph, never a transcript. */
export const MEMORY_SUMMARY_MAX = 1200;

/**
 * >>> **HOW MANY ITEMS RIDE INTO A CONVERSATION, AND WHY THERE IS A CEILING AT ALL.** <<<
 *
 * A member who has talked to Lala for a year has hundreds of these. Handing over all of them costs
 * money on EVERY turn and buries the recent ones, which are the ones her sentence is about
 * (*"last time you were worried about the job"*). Newest first, capped, and the cap is declared here
 * so the store and the prompt cannot disagree about it.
 */
export const MEMORY_ITEM_LIMIT = 24;
/** How many thread summaries ride. Her word is "a rolling summary", so it is a few, not a shelf. */
export const MEMORY_SUMMARY_LIMIT = 4;

/**
 * >>> **HER CLAUSE 5, AND IT IS THE WHOLE COST STORY: A THREAD IS SUMMARISED ONCE.** <<<
 *
 * Answers whether this conversation should be summarised NOW. It is a pure decision over facts the
 * caller already has, so the expensive question (*do we pay for a summary?*) is answered without
 * reading a single raw turn.
 *
 * - Already summarised at this length: NO. That is the "once" in her clause — a thread that has not
 *   grown since its summary has nothing new to say.
 * - Too short to be a conversation: NO. Two turns of "hi" is not a thread anyone needs remembered,
 *   and paying to find that out is the cost her clause is about.
 * - Still being typed in: NO. A thread is summarised when it CLOSES, and the caller's signal for
 *   that is the member having moved on — a new conversation, or a long enough gap.
 */
export const MEMORY_MIN_TURNS = 4;
export const MEMORY_IDLE_MS = 30 * 60 * 1000;

/**
 * >>> **IT #320 / #322 / #324 — HOW MANY TURNS THE SUMMARISER READS, AND WHICH END THEY COME FROM.**
 * <<< (2026-09-30)
 *
 * The summariser read the FIRST sixty turns of a thread, every time. A thread re-summarised as it
 * grew was re-summarised from the same sixty, so Na'ilah's 606-message working thread was remembered
 * forever as it stood on 20 August, and its week-one loops (*"start the roots session tonight,
 * Thursday, inside the late August window"*) were rewritten ten times, their `seen_count` climbing
 * until they ranked FIRST in every other thread's memory. Lala asked her week-one question five
 * weeks in. **It reads the NEWEST sixty now**: the part of a thread she is living in. The earlier
 * summary already carried the beginning.
 */
export const MEMORY_SUMMARY_TURNS = 60;

/** A subject as the store keys it (`subject_key`): trimmed and lowercased, nothing else. */
export const memorySubjectKey = (subject) => String(subject || "").trim().toLowerCase();

// >>> **IT #297 — A QUESTION SHE ANSWERED IN A NEWER THREAD WAS NEVER CLOSED.** <<<
//
// Her store, 2026-09-30: 109 open loops, 0 ever retired. "Did you stay in it longer this time?" was
// asked on 09-19, 09-20 and 09-20 again, each time in a NEW conversation, from a 09-05 loop. #320's
// retirement reads only the thread being summarised again, and a member who starts a fresh thread
// most days never re-summarises the old one, so a loop she answers elsewhere lives for ever. Now the
// writer is shown her open loops from EARLIER threads, numbered, and names the ones she answered,
// did or dropped in this one; those retire through the same reversible `forgotten_at`.

/** How many of her earlier open loops the writer is shown. The newest first; declared, not typed twice. */
export const EARLIER_LOOPS_MAX = 12;

/** The numbered list the writer reads. Empty when there is nothing earlier to close. */
export function earlierLoopsBlock(loops = []) {
  const list = (Array.isArray(loops) ? loops : []).filter((l) => l && l.subject).slice(0, EARLIER_LOOPS_MAX);
  if (!list.length) return "";
  const lines = list.map((l, i) => `${i + 1}. ${String(l.subject).trim()}${l.detail ? `: ${String(l.detail).trim()}` : ""}`);
  return `Before this conversation she had said these things were coming up. Numbered:\n${lines.join("\n")}`;
}

/**
 * The loop ids the writer said she closed. Only whole numbers that name a line it was SHOWN, so a
 * number it invented, a string, or a line past the list retires nothing (presence, never coercion).
 */
export function resolvedEarlierLoopIds(resolved = [], loops = []) {
  const list = (Array.isArray(loops) ? loops : []).filter((l) => l && l.subject).slice(0, EARLIER_LOOPS_MAX);
  const out = new Set();
  for (const n of Array.isArray(resolved) ? resolved : []) {
    if (typeof n !== "number" || !Number.isInteger(n) || n < 1 || n > list.length) continue;
    const id = Number(list[n - 1].id);
    if (Number.isSafeInteger(id) && id > 0) out.add(id);
  }
  return [...out];
}

/**
 * >>> **WHICH OF A THREAD'S OPEN LOOPS ARE RETIRED WHEN THE THREAD IS SUMMARISED AGAIN.** <<<
 *
 * A loop is a thing she said she would do. When the same thread is read again, its newest turns say
 * what is still open, and a loop from that thread the new reading did not restate is done, moved
 * past or superseded: left standing it is the question Lala keeps circling back to. **Only loops,
 * only from THIS thread, only on a RE-summary** (a first summary has nothing to supersede), and
 * never one the new reading restated. Situations and people are not loops and are never retired.
 */
export function loopsToRetire({ standing = [], restated = [], conversationId = null, resummary = false } = {}) {
  if (!resummary) return [];
  const cid = conversationId === null || conversationId === undefined ? null : Number(conversationId);
  const kept = new Set((Array.isArray(restated) ? restated : [])
    .filter((i) => i && String(i.kind || "").trim().toLowerCase() === "loop")
    .map((i) => memorySubjectKey(i.subject)));
  return (Array.isArray(standing) ? standing : [])
    .filter((i) => i && i.kind === "loop" && !i.forgottenAt)
    .filter((i) => (i.conversationId === null || i.conversationId === undefined ? null : Number(i.conversationId)) === cid)
    .filter((i) => !kept.has(memorySubjectKey(i.subject)))
    .map((i) => Number(i.id))
    .filter((id) => Number.isSafeInteger(id) && id > 0);
}

export function memoryNeedsSummary({ turns = 0, summarisedTurns = null, lastMessageAt = null, now = Date.now(), closed = false } = {}) {
  const total = Number(turns);
  if (!Number.isFinite(total) || total < MEMORY_MIN_TURNS) return { should: false, reason: "too-short" };
  const already = summarisedTurns === null || summarisedTurns === undefined ? -1 : Number(summarisedTurns);
  if (Number.isFinite(already) && already >= total) return { should: false, reason: "already-summarised" };
  if (closed) return { should: true, reason: "thread-closed" };
  // >>> **A MISSING TIMESTAMP IS NOT AN OLD ONE, AND THE FIXTURE CAUGHT THIS ONE ON ITS FIRST RUN.**
  // <<< `Number(null)` is 0, which is a FINITE number thirty years in the past — so a caller that
  // simply had no last-message time was told the thread had been idle since 1970 and this answered
  // SPEND. Presence first, never coercion (R53/R136), and on the one decision in this feature that
  // costs money.
  if (lastMessageAt === null || lastMessageAt === undefined || lastMessageAt === "") {
    return { should: false, reason: "no-last-message" };
  }
  const last = lastMessageAt instanceof Date ? lastMessageAt.getTime() : Number(lastMessageAt);
  if (!Number.isFinite(last)) return { should: false, reason: "no-last-message" };
  if (now - last < MEMORY_IDLE_MS) return { should: false, reason: "still-open" };
  return { should: true, reason: "idle" };
}

/**
 * >>> **HER CLAUSE 4 — MEMORY NEVER NARRATES MACHINERY.** <<< *"I remember you told me", never "my
 * records show".* This is the same law the confidentiality gate holds everywhere else, pointed at
 * the one new place a leak can appear: a recall sentence.
 *
 * It is a roster of PHRASINGS rather than a judgement, because the failure is a specific set of
 * words a model reaches for when it is handed a block of stored facts.
 */
const MACHINERY_PHRASES = [
  { label: "records", re: /\b(?:my|our|the)\s+(?:records?|logs?|files?|database|system|notes on file)\b/i },
  { label: "stored", re: /\b(?:stored|saved|logged|on file|in my memory bank|in the system)\b/i },
  { label: "retrieval", re: /\b(?:according to|based on)\s+(?:my|our|the)\s+(?:data|record|memory|history|notes)\b/i },
  { label: "context", re: /\b(?:context (?:window|block)|memory layer|summary of (?:your|our) (?:past )?(?:threads|conversations))\b/i },
  { label: "profile", re: /\byour (?:profile|file|record) (?:says|shows|has)\b/i },
];

export function memoryNarrationViolations(text) {
  const s = String(text || "");
  if (!s.trim()) return [];
  const hits = [];
  for (const rule of MACHINERY_PHRASES) {
    const m = rule.re.exec(s);
    if (m) hits.push({ label: `memory-machinery:${rule.label}`, match: m[0].slice(0, 60) });
  }
  return hits;
}

/**
 * >>> **HER CLAUSE 3 — THE ACCURACY GATE. SHE RECALLS WHAT WAS SAID AND NEVER INVENTS A PAST.** <<<
 *
 * **THIS IS A CONTAINMENT TEST AND NOT A TRUTH TEST, WHICH IS SAID OUT LOUD SO NOBODY LATER MISTAKES
 * IT FOR ONE (R18).** It cannot know whether a member really lost her job. What it CAN know is
 * whether a sentence claiming *you told me X* has any X in the memory it was handed — a recall of
 * something that is not in the memory is invented BY CONSTRUCTION, which is exactly the failure her
 * clause names.
 *
 * A reply with no recall claim in it is clean and costs nothing, which is most replies.
 */
const RECALL_FRAMES = [
  /\byou (?:told me|said|mentioned|were saying|brought up)\b([^.!?]{0,160})/gi,
  /\blast time,?\s+you\b([^.!?]{0,160})/gi,
  /\bwhen we (?:last )?(?:talked|spoke)\b([^.!?]{0,160})/gi,
  /\bi remember (?:you|that you|when you)\b([^.!?]{0,160})/gi,
];

/** The meaning-carrying words of a phrase, for the containment test. */
const RECALL_STOP = new Set(("a about after all and any are as at be been being but by did do does for from get "
  + "had has have how i if in into is it its just like made make me more much my no not now of on one only or "
  + "our out over own said say see she so some still such than that the their them then there these they this "
  + "those to too under up very was way we were what when where which while who why will with would you your "
  + "were been going really things thing something anything").split(/\s+/));

const contentWords = (text) => new Set(String(text || "")
  .toLowerCase()
  .replace(/[^a-z\s']/g, " ")
  .split(/\s+/)
  .filter((w) => w.length > 3 && !RECALL_STOP.has(w)));

/** How much of a claimed recall is actually present in the memory it was handed, 0..1. */
export const MEMORY_SUPPORT_SHARE = 0.34;

export function memoryClaimUnsupported(reply, memoryText) {
  const s = String(reply || "");
  if (!s.trim()) return [];
  const have = contentWords(memoryText);
  const hits = [];
  for (const frame of RECALL_FRAMES) {
    frame.lastIndex = 0;
    let m = frame.exec(s);
    while (m) {
      const claim = String(m[1] || "").trim();
      const want = contentWords(claim);
      // A recall with nothing specific in it ("you said that") claims nothing and is not judged.
      if (want.size >= 2) {
        let shared = 0;
        for (const w of want) if (have.has(w)) shared += 1;
        if (shared / want.size < MEMORY_SUPPORT_SHARE) {
          hits.push({ label: "memory-invented", match: m[0].trim().slice(0, 80), share: shared / want.size });
        }
      }
      m = frame.exec(s);
      if (hits.length >= 4) break;
    }
    if (hits.length >= 4) break;
  }
  return hits;
}

/**
 * >>> **WHAT RIDES INTO HER CONTEXT, AND IT IS WRITTEN AS SOMETHING LALA KNOWS RATHER THAN SOMETHING
 * SHE LOOKED UP.** <<< E1's whole finding is that a LABEL decides how a model treats what arrives
 * under it. A block headed *"stored member records"* produces *"my records show"* in the very next
 * paragraph, which is her clause 4's defect arriving by construction.
 *
 * Returns "" when there is nothing to remember, so a first conversation's prompt is byte-identical
 * to what it was before this feature existed.
 */
/**
 * >>> **BUILD THIS BLOCK TO FIT, AND DROP THE OLDEST CONVERSATIONS FIRST (IT #208/#211, 2026-09-07).**
 * <<<
 *
 * **THE DEFECT THIS ANSWERS IS THIS FILE'S OWN COMMENT COMING TRUE THE WRONG WAY.** The per-block
 * cap of four summaries was removed on 2026-09-05 to honour her ruling — *"across threads she
 * carries EVERY summary"* — and the note left behind said the assembled knowing *"still meets ONE
 * ceiling downstream (`assembleKnowing`), which is where truncation belongs."*
 *
 * **`assembleKnowing` DOES NOT TRUNCATE. IT DROPS THE BLOCK WHOLE.** So a member with a long history
 * stopped carrying four summaries and started carrying NONE — and it is worse than a wash, because
 * the block that overflows is by definition the block belonging to the member with the most to
 * remember. **The house remembered the people with least to remember and forgot the regulars.**
 *
 * MEASURED ON THE LIVE GATE LOG BEFORE A LINE OF THIS WAS WRITTEN: **4,915 turns in three days
 * dropped `memory`**, on a budget of 16,000 characters with only 4,339 of them spent. Nothing was
 * short of room. The ranking put memory SECOND of four and it was thrown away while the shelf and
 * the house life — both ranked below it — survived because they were smaller. A ranking that a
 * block's size can defeat is not a ranking.
 *
 * jeanij88@gmail.com, 670 turns in five days, filed two tickets for it in two days: Lala asking her
 * to store people already in her circle (#208) and answers that lose the thread (#211). In her own
 * conversation, to her face: *"Marykutty John doesn't appear in your circle the way I have it"* —
 * Marykutty John is in her circle.
 *
 * **THE OLDEST GO FIRST, AND THE LOSS IS SAID RATHER THAN SILENT.** Trimming from the recent end
 * would leave her met by somebody who remembers last spring and not last Tuesday.
 */
export function memoryBlockFitter({ items = [], summaries = [], name = "" } = {}) {
  return (maxChars) => {
    const room = Number(maxChars);
    if (!Number.isFinite(room) || room <= 0) return "";
    const rows = Array.isArray(summaries) ? summaries : [];
    // Newest last is how they arrive; the oldest are the ones that go.
    for (let drop = 0; drop <= rows.length; drop += 1) {
      const kept = rows.slice(drop);
      const text = memoryBlock({ items, summaries: kept, name, olderNotCarried: drop });
      if (!text) return "";
      if (text.length <= room) return text;
    }
    // Even with no conversations at all it will not fit: the remembered ITEMS alone are over the
    // room left. Hand back nothing rather than a block cut mid-instruction — the closing rules in
    // this block are what stop her performing the memory at the member, and half of them is worse
    // than none of them (R19: the visible failure is the cheaper one).
    return "";
  };
}

/** IT #377: the day a memory's wording was set, as "Sep 13" (UTC, deterministic), or "" when unknown or unreadable. */
export function memoryNotedOn(at) {
  const d = at instanceof Date ? at : (at ? new Date(at) : null);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function memoryBlock({ items = [], summaries = [], name = "", olderNotCarried = 0 } = {}) {
  const live = (Array.isArray(items) ? items : []).filter((i) => i && i.subject && memoryKind(i.kind));
  const threads = (Array.isArray(summaries) ? summaries : []).filter((s) => s && String(s.summary || "").trim());
  if (!live.length && !threads.length) return "";

  const who = String(name || "").trim().split(/\s+/)[0] || "this member";
  const lines = [
    "",
    "",
    `WHAT YOU REMEMBER ABOUT ${who.toUpperCase()} FROM TALKING WITH HER BEFORE (this is YOUR OWN MEMORY of`,
    "conversations the two of you have actually had — it is not a file, a record, a profile or a lookup, and",
    "you never call it one):",
  ];
  // IT #377 (2026-10-11): a memory line carried no date, so "done seven days ago" read as last week for a month and Lala kept
  // asking how the Millionaire Rite went. Each line now carries the day its wording was set, and the rule for reading one.
  if (live.some((i) => memoryNotedOn(i.detailAt))) {
    lines.push("Each memory carries the day you learned it. A word inside one like \"yesterday\", \"tonight\" or \"seven days ago\"",
      "was true on THAT day, not today: count from it.");
  }

  for (const kind of MEMORY_KINDS) {
    const mine = live.filter((i) => i.kind === kind.key).slice(0, MEMORY_ITEM_LIMIT);
    if (!mine.length) continue;
    lines.push("", `${kind.lead}:`);
    for (const item of mine) {
      const detail = String(item.detail || "").trim();
      const noted = memoryNotedOn(item.detailAt);
      lines.push(`  - ${String(item.subject).trim()}${detail ? ` — ${detail}` : ""}${noted ? ` (noted ${noted})` : ""}`);
    }
  }

  if (threads.length) {
    lines.push("", "The last times you talked:");
    // >>> **EVERY SUMMARY, NOT FOUR — HER RULING, 2026-09-05: *"across threads she carries EVERY
    // summary."*** <<< This sliced to `MEMORY_SUMMARY_LIMIT`, which is **FOUR**, so a member with
    // forty past conversations was met by somebody who remembered the last four of them. The
    // assembled knowing still meets ONE ceiling downstream (`assembleKnowing`), which is where
    // truncation belongs; a second cap here was a budget invisible from the block that spends it.
    for (const t of threads) {
      const when = String(t.when || "").trim();
      lines.push(`  - ${when ? `${when}: ` : ""}${String(t.summary).trim()}`);
    }
    // >>> **AND IF THE OLDEST HAD TO BE LEFT OUT, SHE IS TOLD SO RATHER THAN LEFT TO INFER IT.** <<<
    // A block trimmed in silence is a block that reads as complete, and Lala would speak from it as
    // though those conversations never happened — which is the exact wrong that the drop-whole bug
    // produced, arriving in a quieter costume.
    const older = Number(olderNotCarried);
    if (Number.isFinite(older) && older > 0) {
      lines.push(`  (and ${older} older ${older === 1 ? "conversation" : "conversations"} further back than these, which you remember having but not the detail of.)`);
    }
  }

  lines.push(
    "",
    ">>> HOW YOU USE THIS, AND IT IS THE PART THAT MATTERS MOST: ALL OF THIS IS BACKGROUND, NOT SOMETHING YOU",
    "PERFORM. <<< You are not reading her history back to her. You are talking to somebody you know, so you lead",
    "with HER and with what you actually think, and a remembered thing surfaces only where it makes the point",
    "land. One or two touches, woven into the sentence. Everything above can be perfectly true and still be",
    "wrong to say like this:",
    "",
    "  NOT: \"Last time we talked you were carrying a lot, the job, the mortgage, which business to focus on.\"",
    "  YOU: \"You already know which business it is. You were just looking for somebody to say it out loud.\"",
    "",
    "  NOT: \"Your Soul's Purpose Timeline said this and your purpose reading was clear that.\"",
    "  YOU: \"Everything you have pulled lately is pointing the same way.\"",
    "",
    "Never open by recapping what she told you. Never stack three of her things into one sentence. Never name",
    "which reading said what, the way somebody cites a source — you just KNOW it. Never mention the act of",
    "remembering, and never prove you got a detail right: if she corrected you before, the correction is simply",
    "true now and is never referred to again.",
    "",
    "ONE touch, where it serves the point, is the whole of it — \"last time you were worried about the job, how did",
    "that land?\" — and when she opens with something general (\"how am I doing?\", \"what do you see for me?\") ONE",
    "concrete thing you remember belongs in your answer, said in passing. That is what makes it HERS rather than",
    "an answer anybody could have been given. Warmth on its own is not knowing her.",
    "NEVER make her repeat something she has already told you. You may say you remember, and",
    "you NEVER say you have records, files, notes, data, a profile or a system. If something here is out of date,",
    "she will tell you, and her word wins.",
    "",
    "AND YOU ONLY REMEMBER WHAT IS ACTUALLY WRITTEN ABOVE. You never fill in a detail she did not give you,",
    "never guess what happened next, and never invent a past conversation. If you are not sure, ask her.",
  );

  // ===============================================================================================
  // >>> **MEM-1 PRESENCE — HER ORDER OF 2026-09-03: MEMORY BECOMES INITIATIVE, NOT JUST RECALL.** <<<
  //
  // *"Lala PROACTIVELY brings the member's history into conversation, unprompted, like a person who
  //  knows them and has been thinking about them."*
  //
  // **EVERYTHING ABOVE IS PERMISSION AND THIS IS INSTRUCTION, WHICH IS THE WHOLE DIFFERENCE.** The
  // block above says a remembered thing MAY surface where it serves the point. Her order is that a
  // returning member is ASKED about her unfinished business, unprompted, so she feels awaited rather
  // than filed. Recall is passive; presence is a question.
  //
  // >>> **AND IT IS A QUESTION, WHICH IS EXACTLY WHY IT DOES NOT BREAK `memoryRenderingViolations`
  // (`eee006fa`).** <<< That gate refuses a recall FRAME carrying TWO OR MORE remembered subjects in
  // the opening, and an inventory sentence anywhere. Its own comment names the legal shape by
  // example: *"last time you were worried about the job, how did that land?"* — one subject, asked
  // rather than recited. So the directive below is written to that shape deliberately: ONE loop, as a
  // question, never a list. The distinction is the build.
  // ===============================================================================================
  const loops = live.filter((i) => i.kind === "loop");
  lines.push(
    "",
    ">>> AND THE PART THAT IS NOT WAITING TO BE ASKED. <<<",
    "You have been thinking about her. When she comes back to you, you pick something up from last time yourself,",
    "before she asks for anything, the way a friend does who has been wondering how it went.",
    "",
    "IT IS A QUESTION, NEVER A SUMMARY. \"Before anything, how did that money spell land? I've been wondering.\" or",
    "\"Last time the job thing was sitting heavy on you. Where'd that go?\" You are asking about her unfinished",
    "business, not reciting it back. ONE thing, the warmest or the most unfinished one, and then you listen.",
    "",
    "AND THE THINGS SHE SAID SHE WOULD DO ARE YOUR OPEN LOOPS. The spell she was going to work, the conversation",
    "she was going to have, the decision she was sitting on, the answer she was waiting for. You ask how they went.",
    "You celebrate or you steady her depending on what she says, exactly like somebody tracking her life.",
    "",
    "MID-CONVERSATION, when what she is talking about now touches something she carried before, you weave it in as",
    "a knower would: \"this is the same pattern from the situation with your sister, you see that, right?\" Earned,",
    "familiar, never forced.",
    "",
    "THE RELEVANCE LAW GOVERNS ALL OF IT: a memory surfaces only where it genuinely serves this moment. No",
    "memory-dropping for its own sake, and no more than a couple of touches in a whole conversation. Presence is",
    "seasoning, not the meal.",
    "",
    "AND YOU READ THE ROOM BEFORE YOU REACH. If what she carried was a grief, a loss, an illness or something",
    "frightening, you do not cheerfully poke it. You go gently or you say nothing about it at all and simply be",
    "present with her. The rule is a person's instinct, not a script: some things you ask about, and some things",
    "you just hold.",
    "",
    "YOU FOLLOW UP ONLY ON WHAT SHE ACTUALLY SAID. You never invent a thread, never assume how something turned",
    "out, and never ask about a thing she never told you.",
  );
  if (loops.length) {
    lines.push(
      "",
      `RIGHT NOW, the loop most worth asking about is: ${String(loops[0].subject).trim()}${loops[0].detail ? ` — ${String(loops[0].detail).trim()}` : ""}.`,
      "Open on it if the moment allows, in your own words, as a question.",
    );
  }
  return lines.join("\n");
}

/**
 * The flat text of a member's memory, for the accuracy gate to test a recall claim against. Kept here
 * beside the block so the gate and the prompt can never be reading two different memories (R28/R29).
 */
export function memoryText({ items = [], summaries = [] } = {}) {
  return [
    ...(Array.isArray(items) ? items : []).map((i) => `${i?.subject || ""} ${i?.detail || ""}`),
    ...(Array.isArray(summaries) ? summaries : []).map((s) => String(s?.summary || "")),
  ].join(" ").trim();
}

/** A memory item as it arrives from the summariser, refused rather than trimmed into shape. */
export function memoryItemVerdict(input) {
  const { kind, subject, detail } = input && typeof input === "object" ? input : {};
  if (!memoryKind(kind)) return { ok: false, reason: "kind" };
  const subj = typeof subject === "string" ? subject.trim() : "";
  if (!subj || subj.length > MEMORY_SUBJECT_MAX) return { ok: false, reason: "subject" };
  const det = typeof detail === "string" ? detail.trim() : "";
  if (det.length > MEMORY_DETAIL_MAX) return { ok: false, reason: "detail" };
  return { ok: true, reason: "" };
}

/**
 * >>> **HER CLAUSE 6 — MEMORY IS BACKGROUND, NOT PERFORMANCE.** <<< (owner, 2026-09-02)
 *
 * **THE EXHIBIT IS HER OWN LIVE CONVERSATION**, and one of the sentences in it is the one this house
 * reported to her as MEM-1's PROOF the same morning:
 *
 *     "Last time we talked you were carrying a lot, the job situation, the mortgage, trying to
 *      figure out which business to focus on. How has all of that been sitting with you?"
 *     "you corrected me mid-conversation on the reversal, only the Seven of Pentacles was reversed"
 *     "your Soul's Purpose Timeline says... and the What is my purpose reading was clear that..."
 *
 * Every one of those is ACCURATE. That is the whole problem: **she is reading a record back rather
 * than speaking as somebody who knows her.** Her words: *she speaks as someone who simply KNOWS
 * them, the way a friend who was there references the past — naturally, partially, woven into the
 * advice, never inventoried.*
 *
 * **THE SUMMARIES UNDERNEATH MAY STAY AS DETAILED AS THEY LIKE. THIS IS A RENDERING LAW** — it
 * governs what she DOES with them:
 *   - lead with the PERSON and the COUNSEL;
 *   - remembered facts surface only where they SERVE the point, one or two touches;
 *   - never a recap paragraph, never an inventory sentence;
 *   - never cite which reading said what, like sources — *"everything you've pulled lately is
 *     pointing the same way"*, not *"your X reading said, and your Y reading said"*;
 *   - never narrate the act of remembering, and never demonstrate accuracy for its own sake.
 *
 * **WHY THE COUNT IS TAKEN AGAINST THE MEMORY ITSELF RATHER THAN AGAINST A WORD LIST.** "Inventory"
 * is not a phrasing, it is a DENSITY — three of the things she told you, stacked into one sentence,
 * reads as minutes however warmly it is worded. So the items Lala was actually handed are what get
 * counted, which also means this gate says nothing at all on a turn carrying no memory.
 */

/**
 * >>> **WHAT A REMEMBERED SUBJECT MAY BE COUNTED BY, AND WHAT IT MAY NOT (her COST1-Q1 YES).** <<<
 * Her words: *"Count each distinct word or phrase once, not once per matching memory; drop pronouns
 * and everyday vocabulary from what counts; ignore words from the member's own message. The 'one or
 * two touches' limit and every other memory rule stay exactly as they are."*
 *
 * **MEASURED BEFORE A WORD OF THIS WAS WRITTEN** (COST-1 P2-7, re-measured 2026-10-01 on the 11,677
 * flags of 09-24..09-30, every one of them a paid rewrite): the keys that carried the flags were
 * "working" 8,092 times, "candle" 4,713, "work" 2,501, "altar" 1,352, "petition" 746, "seal" 711,
 * "her" 466, "jar" 410 — the words of the WORK she is doing with Lala, and pronouns, not three things
 * she told her. A member with five candle workings remembered had any sentence about a candle
 * counted as five recollections.
 *
 * So these words never count, and they are the families she named and nothing wider:
 *   - PRONOUNS and person-filler (her, him, them, man, woman...);
 *   - TIME WORDS (days, weekdays, months, tonight, before, after, first, small numbers...);
 *   - EVERYDAY AND WORKING VOCABULARY: the words of the practice Lala talks in every answer
 *     (working, candle, altar, petition, seal, jar, oil, ritual, bath, card, reading, the planets
 *     and the moon) and the most ordinary words (new, real, right, back, call...).
 * **HER SUBJECTS STAY COUNTABLE**: names, the job, the mortgage, the business, the move, the
 * divorce, money, love, the court case. A memory made only of working words ("the candle working")
 * cannot be counted by a sentence, because nothing in it says which of her things it reached for.
 */
const SUBJECT_EVERYDAY = new Set((
  // pronouns and person-filler
  "her hers him his she they them their theirs himself herself themselves yourself mine yours "
  + "man woman guy girl lady person people someone somebody everyone everybody "
  // time words
  + "today tonight tomorrow yesterday morning evening night nights day days week weeks weekend month "
  + "months year years time times hour hours minute minutes moment lately recently soon ago before after "
  + "first last next past future again always never ever early late daily weekly "
  + "monday tuesday wednesday thursday friday saturday sunday january february march april june july "
  + "august september october november december "
  + "two three four five six seven eight nine ten eleven twelve "
  // everyday vocabulary
  + "new old real right back full big small little good bad call called part side place piece kind "
  + "start started still keep kept going come came take took give gave feel feeling feelings know "
  + "think want need help lot lots whole thing things stuff something everything "
  // working vocabulary: the practice Lala speaks in
  + "work working workings worked candle candles altar altars petition petitions seal seals jar jars oil "
  + "oils blend blends ritual rituals rite rites spell spells bath baths cleanse cleansing purge "
  + "card cards deck reading readings pull pulled chart charts energy intention intentions practice "
  + "prayer prayers burn burning wish wishes setup stack plate plates pot root crystal crystals herbs "
  + "activation alchemy magic tarot moon sun mercury venus mars jupiter saturn uranus neptune pluto "
  + "planet planets sign signs house houses"
).split(/\s+/).filter(Boolean));

const everyday = (w) => SUBJECT_EVERYDAY.has(w) || (w.endsWith("s") && SUBJECT_EVERYDAY.has(w.slice(0, -1)));

// A possessive is the same word ("Tuesday's" is Tuesday, "Simi's" is Simi), and an apostrophe
// inside a word is dropped, so a subject and a sentence fold the same way.
const foldText = (text) => String(text || "").toLowerCase()
  .replace(/['’]s\b/g, "")
  .replace(/['’]/g, "");
const wordsOf = (text) => foldText(text)
  .replace(/[^a-z\s]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

/** A remembered subject reduced to the words that carry it. "her sister Denise" -> sister, denise. */
const subjectKeys = (subject) => {
  const bare = wordsOf(subject).replace(/^(?:the|a|an|her|his|their|my|your)\s+/, "").trim();
  if (!bare) return [];
  // >>> **EVERY CONTENT WORD, NOT THE LONGEST ONE, AND HER OWN LIVE REPLY IS WHY.** <<< This took
  // only the longest word beside the whole phrase, so the subject `carrying the mortgage alone`
  // offered `carrying` — and a reply saying "the mortgage" matched NEITHER key. The count came back
  // 2 where it should have been 3, and the live answer walked straight through the clause written
  // for it while the fixture's own constructed sentences all passed. A sentence reuses whichever of
  // her words it likes, so all of them are keys, EXCEPT the everyday and working words above,
  // which say nothing about which of her things a sentence reached for (COST1-Q1).
  const words = bare.split(" ").filter((w) => w.length >= 3 && !RECALL_STOP.has(w) && !everyday(w));
  // A one-word subject too short for the length floor ("AI") is still her subject.
  if (!words.length && !bare.includes(" ") && !RECALL_STOP.has(bare) && !everyday(bare)) return [bare];
  return [...new Set(words)];
};

/**
 * How many DISTINCT remembered subjects one sentence reaches for.
 *
 * >>> **EACH WORD OF THE SENTENCE COUNTS ONCE, NOT ONCE PER MEMORY IT MATCHES (COST1-Q1).** <<< Five
 * memories sharing one word used to make that one word five recollections. Now every remembered
 * subject must be reached through a word of its OWN: the count is the largest set of her memories
 * that can each be paired with a different word of the sentence, so three memories reached through
 * one shared word count one, and her exhibit (the job, the mortgage, which business) still counts
 * three. **A PHRASE IS ONE**: matched words standing side by side in the sentence, with no comma or
 * stop between them, are one thing said ("Michael Jerome Walker Junior" is one man, however many of
 * her memories carry a piece of his name). **Words from her own message do not count at all**
 * (`message`): when she asks about her job and her mortgage, Lala answering about them is answering
 * her, not reciting her.
 */
export function rememberedSubjectsIn(sentence, items = [], { message = "" } = {}) {
  const hers = new Set(wordsOf(message).split(" ").filter(Boolean));
  const said = (w) => hers.has(w) || hers.has(`${w}s`) || (w.endsWith("s") && hers.has(w.slice(0, -1)));
  const keysOf = (Array.isArray(items) ? items : []).map((item) => subjectKeys(item && item.subject).filter((w) => !said(w)));
  const anyKey = new Set(keysOf.flat());
  // The sentence's words, cut at every mark of punctuation, and each unbroken run of remembered words
  // made ONE token: the phrase is what was said, once.
  const tokenOf = new Map();
  let runs = 0;
  for (const chunk of foldText(sentence).split(/[^a-z\s]+/)) {
    let inRun = false;
    for (const w of chunk.split(/\s+/).filter(Boolean)) {
      if (anyKey.has(w)) {
        if (!inRun) { runs += 1; inRun = true; }
        if (!tokenOf.has(w)) tokenOf.set(w, new Set());
        tokenOf.get(w).add(runs);
      } else {
        inRun = false;
      }
    }
  }
  // For each memory, the phrases of the sentence that reach it.
  const reach = [];
  for (const keys of keysOf) {
    const runsHit = new Set();
    for (const w of keys) for (const r of tokenOf.get(w) || []) runsHit.add(r);
    if (runsHit.size) reach.push([...runsHit]);
  }
  // The largest pairing of memories to distinct words (augmenting paths over a handful of items).
  const owner = new Map();
  const claim = (i, seen) => {
    for (const w of reach[i]) {
      if (seen.has(w)) continue;
      seen.add(w);
      if (!owner.has(w) || claim(owner.get(w), seen)) { owner.set(w, i); return true; }
    }
    return false;
  };
  let n = 0;
  for (let i = 0; i < reach.length; i += 1) if (claim(i, new Set())) n += 1;
  return n;
}

/** Openings that announce a recap before a single word of counsel has arrived. */
const RECAP_FRAMES = [
  /^\s*(?:so\s+)?last time\b/i,
  /^\s*(?:so\s+)?when we (?:last )?(?:talked|spoke)\b/i,
  /^\s*(?:so\s+)?you (?:told me|mentioned|said|were saying|brought up)\b/i,
  /^\s*i remember\b/i,
  /^\s*(?:so\s+)?catching up\b/i,
];

/** Citing a reading like a source, and narrating the act of remembering. */
const PERFORMANCE_PHRASES = [
  {
    label: "cites-a-reading",
    re: /\b(?:your|the)\s+[A-Z][\w'-]*(?:\s+(?:[A-Z][\w'-]*|of|is|my|the|and|to|what))*\s+(?:reading|report|record|timeline|chart)\s+(?:said|says|showed|shows|told|was clear|made\s+(?:it\s+)?clear|pointed)\b/,
  },
  {
    label: "cites-a-reading",
    re: /\b(?:your|the)\s+(?:soul'?s?\s+purpose\s+timeline|soul\s+record|soul\s+contracts|past[-\s]life\s+(?:gifts|connections)|human\s+design|numerology|birth\s+chart)\s+(?:said|says|showed|shows|told|was\s+clear|made\s+(?:it\s+)?clear)\b/i,
  },
  // >>> THE ALTERNATION USED TO RUN STRAIGHT INTO `me` WITH NO SPACE — `correctedme` — so HER OWN
  // EXHIBIT SENTENCE walked through the clause written for it, and the roster looked complete.
  // Found by driving her words rather than by reading the regex (R63/R134).
  { label: "narrates-remembering", re: /\byou\s+(?:corrected|caught|clarified(?:\s+for)?)\s+me\b/i },
  { label: "narrates-remembering", re: /\byou\s+pointed\s+out\s+to\s+me\b/i },
  {
    label: "narrates-remembering",
    re: /\b(?:if\s+i\s+remember\s+(?:correctly|right)|to\s+be\s+accurate|just\s+to\s+(?:be\s+precise|confirm\s+what\s+you\s+said)|for\s+the\s+record)\b/i,
  },
];

/**
 * >>> **HOW MANY REMEMBERED TOUCHES ONE SENTENCE MAY CARRY.** <<< Her words are "one or two", so the
 * ceiling is two and the third is what turns counsel into minutes.
 */
export const MEMORY_INVENTORY_MAX = 2;

/**
 * >>> **THE RENDERING GATE — what makes this reply read like MINUTES rather than like COUNSEL.** <<<
 * Deterministic, and it costs nothing on a clean reply, which is almost every reply.
 */
export function memoryRenderingViolations(reply, { items = [], message = "" } = {}) {
  const s = String(reply || "").trim();
  if (!s) return [];
  const hits = [];

  for (const rule of PERFORMANCE_PHRASES) {
    const m = rule.re.exec(s);
    // >>> ONE HIT PER LABEL. <<< Four matches of one fault is one fault to fix, and a rewrite
    // instruction listing it four times reads as four different problems (R18).
    if (m && !hits.some((h) => h.label === `memory-${rule.label}`)) {
      hits.push({ label: `memory-${rule.label}`, match: m[0].trim().slice(0, 70) });
    }
  }

  // A turn carrying no memory cannot be inventorying one, so the density clauses say nothing at all.
  const live = (Array.isArray(items) ? items : []).filter((i) => i && i.subject);
  if (!live.length) return hits;

  const sentences = s.split(/(?<=[.!?])\s+/).filter((t) => t.trim());

  // **THE RECAP OPENING.** A recall frame is NOT the fault — *"last time you were worried about the
  // job, how did that land?"* is her own example of the right thing and it names ONE. The fault is
  // opening on a recall frame and then listing what she told you, before any counsel arrives.
  //
  // >>> **THE OPENING IS THE FIRST TWO SENTENCES, NOT THE FIRST, AND HER LIVE REPLY IS WHY.** <<<
  // It opened *"That's a good question, and honestly I'd rather hear it from you first."* and put
  // the recital in sentence TWO. A pleasantry in front of a recap is still a recap opening, and
  // reading only sentence one made this clause trivially avoidable by saying hello first.
  for (const sentence of sentences.slice(0, 2)) {
    if (RECAP_FRAMES.some((re) => re.test(sentence)) && rememberedSubjectsIn(sentence, live, { message }) >= 2) {
      hits.push({ label: "memory-recap-opening", match: sentence.trim().slice(0, 90) });
      break;
    }
  }

  // **THE INVENTORY SENTENCE**, wherever it lands in the reply.
  for (const sentence of sentences) {
    const n = rememberedSubjectsIn(sentence, live, { message });
    if (n > MEMORY_INVENTORY_MAX) {
      hits.push({ label: "memory-inventory", match: sentence.trim().slice(0, 90), subjects: n });
      break;
    }
  }

  return hits;
}
